import Phaser from 'phaser';
import { getCurrentTheme } from '../Utils.js';

// Array of loading tips/flavor text
const loadingTips = [
  "Gathering the bravest warriors...",
  "Loading battle arenas...",
  "Sharpening swords and shields...",
  "Summoning legendary champions...",
  "Preparing the ultimate showdown...",
  "Assembling the battlefield...",
  "Forging weapons of destiny...",
  "Awakening ancient powers...",
  "Calibrating combat algorithms...",
  "Syncing player avatars...",
  "Initializing victory conditions...",
  "Charging energy shields...",
  "Loading tactical maneuvers...",
  "Preparing epic confrontations...",
  "Unleashing warrior spirits..."
];

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreloadScene' });
    this.progressBar = null;
    this.progressBox = null;
    this.loadingText = null;
    this.tipText = null;
    this.currentTip = 0;
    this.tipTimer = 0;
    this.startTime = 0;
    this.gameStartDelay = 10;
    this.loadingComplete = false;
    // Smooth progress value for tweened filling
    this.progress = { value: 0 };
    // Bar sizing (double width, half height)
    this.BAR_WIDTH = 360;
    this.BAR_HEIGHT = 12;
  }

  init(data) {
    // Store configuration data passed from InstructionsScene
    if (data) {
      console.log('[PreloadScene] init received config:', data);
      this.data.set('config', data);
    }
  }

  preload() {
    // Set CORS mode for all asset loading (required for S3)
    this.load.setCORS('anonymous');
    this.load.crossOrigin = 'anonymous';
    
    // Theme should already be loaded and available via getCurrentTheme()
    const theme = getCurrentTheme();

    if (!theme) {
      console.error('❌ PreloadScene: No theme loaded!');
      // Fallback to basic loading
      this.loadFallbackAssets();
      return;
    }

    console.log(`✅ PreloadScene: Using theme - ${theme.name}`);
    console.log('🎯 PreloadScene: theme', theme);

    try {
      // Load UI assets from theme
      const uiAssets = theme.ui || {};

      // Determine S3 base path for any theme; fallback to local assets if unavailable
      const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      const s3BasePath = (theme && theme.s3BasePath)
        ? theme.s3BasePath
        : (theme && theme.id)
            ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`
            : '';
      const useS3 = !!s3BasePath;

      // Load preload background
      const preloadBg = uiAssets.preloadBackground || 'assets/menus/PreloadBackground.jpg';
      this.load.image('preloadBackground', preloadBg);

      // Load coin logo
      const coinLogo = uiAssets.coinLogo || 'assets/menus/coin_logo1.png';
      this.load.image('coin_logo1', coinLogo);

      // Load player textures (prefer normalized theme.players, fallback to legacy assets.players)
      const playerAssets = (Array.isArray(theme.players) && theme.players.length)
        ? theme.players
        : (theme.assets?.players || []);
      console.log('🎯 PreloadScene: player assets found:', playerAssets.length, playerAssets);
      playerAssets.forEach(playerAsset => {
        // Allow either string filenames or object entries with an image field
        const filename = typeof playerAsset === 'string' ? playerAsset : (playerAsset && (playerAsset.image || playerAsset.asset || ''));
        const safeFilename = String(filename || '');
        const assetKey = safeFilename.replace(/\.[^/.]+$/, ''); // Remove extension for key

        // Use S3 path for custom themes, local path for default theme
        const assetPath = useS3
          ? `${s3BasePath}/players/${safeFilename}`
          : `assets/players/${safeFilename}`;

        //console.log('📥 PreloadScene: queue player image', { assetKey, assetPath, useS3 });
        this.load.image(assetKey, assetPath);
      });

      // Load battlefield images from normalized theme (active only), fallback to legacy assets list
      const battlefieldImages = Array.isArray(theme.battlefields)
        ? theme.battlefields.filter(b => b && b.active && b.image).map(b => b.image)
        : (theme.assets?.battlefields || []);
      console.log('🏞️ PreloadScene: battlefield images queued:', battlefieldImages);
      battlefieldImages.forEach(img => {
        const key = typeof img === 'string' ? img : String(img || '');

        // Use S3 path for custom themes, local path for default theme
        const assetPath = useS3
          ? `${s3BasePath}/battlefields/${key}`
          : (key.startsWith('battlefields/') ? `assets/${key}` : `assets/battlefields/${key}`);

        //console.log('🏞️ PreloadScene: queue battlefield image', { key, assetPath, useS3 });
        this.load.image(key, assetPath);
      });

      // Load gubbins assets (always use local assets for game essentials)
      this.load.image('skullbones1', 'assets/gubbins/skullbones1.png');
      this.load.image('blood1', 'assets/gubbins/blood1.png');
      this.load.image('cloud1', 'assets/gubbins/cloud1.png');
      this.load.image('fog1', 'assets/gubbins/fog1.png');

      this.load.image('default-battlefield', 'assets/battlefields/default-arena.jpg');

      // Load food assets from theme
      const foodItems = theme.food || [];
      console.log('[PreloadScene] loading food items:', foodItems);
      foodItems.forEach(food => {
        // Use S3 path for custom themes, local path for default theme
        
        if (food.spriteKey) {
          const assetPath = useS3
          ? `${s3BasePath}/items/food/${food.spriteKey}`
          : `assets/items/food/${food.spriteKey}`;
          console.log('[PreloadScene] loading food image:', food.spriteKey, assetPath);
          this.load.image(food.spriteKey, assetPath);
        }
      });

      // Load shield assets from theme
      const shields = theme.shields || [];
      shields.forEach(shield => {
        if (shield.asset) {
          this.load.image(shield.id, `assets/items/shields/${shield.asset}`);
        }
      });

      // Load particle textures from theme
      const particles = uiAssets.particles || {};
      if (particles.white) this.load.image('particle-white', particles.white);
      if (particles.yellow) this.load.image('particle-yellow', particles.yellow);
      if (particles.grey) this.load.image('particle-grey', particles.grey);
      if (particles.red) this.load.image('particle-red', particles.red);
      this.load.image('star_particle', 'assets/particles/star_particle.png');

      // Load item icons (potions) - these are static for now
      const potionColors = ['red', 'purple', 'green', 'blue'];
      potionColors.forEach(color => {
        for (let i = 0; i <= 4; i++) {
          const key = `equip_potion_${color}_${i}`;
          this.load.image(key, `assets/items/${key}.png`);
        }
      });

      // Load item icons from theme.items using iconKey (from theme_items table)
      if (theme.items && Array.isArray(theme.items)) {
        theme.items.forEach(item => {
          const iconKey = item?.iconKey;
          if (!iconKey) return;
          const fileName = iconKey.endsWith('.png') ? iconKey : `${iconKey}.png`;
          const assetPath = useS3
            ? `${s3BasePath}/items/${fileName}`
            : `assets/items/${fileName}`;
          try {
            this.load.image(iconKey, assetPath);
            console.log('[PreloadScene] loading item icon:', iconKey, assetPath);
          } catch (err) {
            console.warn('[PreloadScene] failed to load item icon:', iconKey, err);
          }
        });
      }

      // Load other game assets
      this.load.image('deathMarker', 'https://play.rosebud.ai/assets/deathMarker.png?9Bn9');
      this.load.image('matchEndedScreen', 'https://play.rosebud.ai/assets/matchEndedScreen.png?pmin');
      // Arrow projectile sprite (used by ArrowManager for the actual projectile)
      this.load.image('arrow-projectile', 'assets/items/projectiles/arrow.png');

      // Intermission and battle music are loaded on-demand to save memory

      // Load all SFX audio files from theme
      const sounds = theme.sounds || {};
      let totalSfxCount = 0;
      
      Object.keys(sounds).forEach(category => {
        const entries = sounds[category] || [];
        entries.forEach(entry => {
          const sfxEntry = (typeof entry === 'object' && entry) ? entry : { file: entry };
          const file = sfxEntry.file || entry;
          const isDefault = sfxEntry.default === true;
          
          if (file) {
            // Determine the correct S3 path based on whether it's a default sound
            let sfxPath;
            if (isDefault) {
              // Default sounds come from the default theme's S3 path
              sfxPath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/soulwars-default/${file}`;
            } else if (useS3) {
              // Custom theme sounds from current theme's S3 path
              sfxPath = `${s3BasePath}/${file}`;
            } else {
              // Fallback to local assets
              sfxPath = `assets/${file}`;
            }
            
            // Use the file path as the key (for compatibility with SfxManager)
            this.load.audio(file, sfxPath);
            totalSfxCount++;
          }
        });
      });
      
      console.log(`🔊 PreloadScene: Queued ${totalSfxCount} SFX audio files for preloading`);

      // Create confetti texture
      const graphics = this.make.graphics();
      graphics.fillStyle(0xffffff);
      graphics.fillRect(0, 0, 8, 8);
      graphics.generateTexture('confetti', 8, 8);
      graphics.destroy();

      console.log('🎯 PreloadScene: All assets queued for loading');

    } catch (error) {
      console.error('❌ PreloadScene: Failed to load theme assets:', error);
      // Fallback to loading minimal assets
      this.loadFallbackAssets();
    }

    // Set up loading progress events
    this.load.on('progress', this.onProgress, this);
    this.load.on('complete', this.onComplete, this);
    
    // Add detailed file loading event handlers
    this.load.on('filecomplete', (key, type, data) => {
      //console.log(`✅ PreloadScene: loaded ${type}:`, key);
    });
    
    this.load.on('loaderror', (file) => {
      console.error(`❌ PreloadScene: failed to load ${file.type}:`, file.key, 'from:', file.url);
    });
  }

  loadFallbackAssets() {
    console.log('🔄 PreloadScene: Loading fallback assets...');

    this.load.image('preloadBackground', 'assets/menus/PreloadBackground.jpg');
    this.load.image('coin_logo1', 'assets/menus/coin_logo1.png');

    // Load basic assets as fallback
    const basicPlayers = ['easy1', 'normal1', 'hard1', 'elite1'];
    basicPlayers.forEach(texture => {
      const assetPath = `assets/players/${texture}.png`;
      console.log('📥 PreloadScene (fallback): queue player image', { assetKey: texture, assetPath });
      this.load.image(texture, assetPath);
    });

    // Load basic battlefields
    //this.load.image('battlefields/dungeon1.jpg', 'assets/battlefields/dungeon1.jpg');
    //this.load.image('battlefields/dungeon2.jpg', 'assets/battlefields/dungeon2.jpg');
    //this.load.image('battlefields/dungeon3.jpg', 'assets/battlefields/dungeon3.jpg');

    // Basic gubbins
    this.load.image('skullbones1', 'assets/gubbins/skullbones1.png');
    this.load.image('blood1', 'assets/gubbins/blood1.png');
    this.load.image('cloud1', 'assets/gubbins/cloud1.png');
    this.load.image('fog1', 'assets/gubbins/fog1.png');

    // Basic food
    this.load.image('food_melon', 'assets/items/food/melon.png');
    this.load.image('food_chicken', 'assets/items/food/chicken.png');

    // Basic particles
    this.load.image('particle-white', 'assets/particles/white-square.png');
    this.load.image('particle-yellow', 'assets/particles/yellow-square.png');
    this.load.image('star_particle', 'assets/particles/star_particle.png');

    // Create confetti texture
    const graphics = this.make.graphics();
    graphics.fillStyle(0xffffff);
    graphics.fillRect(0, 0, 8, 8);
    graphics.generateTexture('confetti', 8, 8);
    graphics.destroy();
  }

  create() {
    // Record start time for minimum display duration
    this.startTime = this.time.now;

    // Resolve current viewport dimensions and center
    const viewW = this.cameras.main.width;
    const viewH = this.cameras.main.height;
    const centerX = viewW / 2;
    const centerY = viewH / 2;

    // Lock camera to prevent any movement during preload
    this.cameras.main.setBounds(0, 0, viewW, viewH);
    this.cameras.main.setZoom(1);
    this.cameras.main.centerOn(centerX, centerY);
    // Disable camera controls and movement
    this.cameras.main.setScroll(0, 0);

    // Add background image and scale it to cover the viewport (preserve aspect ratio)
    const bg = this.add.image(centerX, centerY, 'preloadBackground').setOrigin(0.5);
    const scaleX = viewW / bg.width;
    const scaleY = viewH / bg.height;
    const scale = Math.max(scaleX, scaleY); // cover viewport
    bg.setScale(scale);
    bg.setDepth(0);

    // Create progress bar container
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(0x222222, 0.8);
    this.progressBox.fillRect(centerX - (this.BAR_WIDTH / 2), centerY + 100, this.BAR_WIDTH, this.BAR_HEIGHT + 11);
    this.progressBox.setDepth(1);

    // Create progress bar
    this.progressBar = this.add.graphics();
    this.progressBar.setDepth(2);

    // Add loading text
    this.loadingText = this.add.text(centerX, centerY + 70, 'Loading...', {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    this.loadingText.setDepth(3);

    // Add tip text at the bottom edge of the screen,based on the size of the gsme window
    let tipTextY = viewH - 20;
    this.tipText = this.add.text(centerX, tipTextY, loadingTips[0], {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#ffff88',
      stroke: '#000000',
      strokeThickness: 1,
      align: 'center'
    }).setOrigin(0.5);
    this.tipText.setDepth(3);
  }

  update(time, delta) {
    // Cycle through tips every 2 seconds
    this.tipTimer += delta;
    if (this.tipTimer >= 2000) {
      this.currentTip = (this.currentTip + 1) % loadingTips.length;
      this.tipText.setText(loadingTips[this.currentTip]);
      this.tipTimer = 0;
    }

    // Check if we should transition to GameScene
    if (this.loadingComplete && (time - this.startTime) >= this.gameStartDelay * 1000) {
      // Pass configuration data to GameScene
      const configData = this.data.get('config') || {};
      console.log('[PreloadScene] starting GameScene with config:', configData);
      this.scene.start('GameScene', configData);
    }
  }

  onProgress(value) {
    if (!this.progressBar) return;

    // Clamp and tween the visual progress for smoother fill even with cached assets
    const v = Phaser.Math.Clamp(value, 0, 1);
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    this.tweens.add({
      targets: this.progress,
      value: v,
      duration: 150,
      ease: 'Linear',
      onUpdate: () => {
        this.progressBar.clear();
        this.progressBar.fillStyle(0xffff00, 1);
        this.progressBar.fillRect(
          centerX - (this.BAR_WIDTH / 2),
          centerY + 105,
          this.BAR_WIDTH * this.progress.value,
          this.BAR_HEIGHT
        );
      }
    });

    // Update loading text
    if (this.loadingText) {
      if (value < 1) {
        this.loadingText.setText(`Loading... ${Math.round(value * 100)}%`);
      } else {
        this.loadingText.setText('Loading complete!');
      }
    }
  }

  onComplete() {
    // Mark loading as complete - update method will handle transition after minimum time
    this.loadingComplete = true;

    // Fill progress bar completely
    if (this.progressBar) {
      const centerX = this.cameras.main.width / 2;
      const centerY = this.cameras.main.height / 2;
      this.tweens.add({
        targets: this.progress,
        value: 1,
        duration: 200,
        ease: 'Linear',
        onUpdate: () => {
          this.progressBar.clear();
          this.progressBar.fillStyle(0xffff00, 1);
          this.progressBar.fillRect(
            centerX - (this.BAR_WIDTH / 2),
            centerY + 105,
            this.BAR_WIDTH * this.progress.value,
            this.BAR_HEIGHT
          );
        }
      });
    }
  }
}
